import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { ExamDialog } from 'components/exam/ExamDialog';

/** Long enough to read three short lines, short enough that nobody waits on a result that is already graded. */
export const ANALYSING_MS = 2400;

const STEPS = ['answers', 'score', 'plan'] as const;

export interface AnalysingResultsDialogProps {
  open: boolean;
  /** Called when the animation ends, and when the learner skips it or presses Escape. */
  onDone: () => void;
}

/** The pause between handing the test in and what comes next. The grading itself has already happened. */
export function AnalysingResultsDialog({ open, onDone }: AnalysingResultsDialogProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    const slice = ANALYSING_MS / STEPS.length;
    const timers = [
      setTimeout(() => setStep(1), slice),
      setTimeout(() => setStep(2), slice * 2),
      setTimeout(onDone, ANALYSING_MS),
    ];
    return () => timers.forEach(clearTimeout);
    // `onDone` is deliberately not a dependency: a new function each render must not restart the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ExamDialog open={open} onClose={onDone} widthClass="max-w-[400px]" withDescription={false}>
      <div className="flex flex-col items-center text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20" aria-hidden>
          <Loader2 className="h-7 w-7 animate-spin text-foreground motion-reduce:animate-none" />
        </span>
        <DialogTitle className="mt-5 text-[22px] font-bold leading-tight tracking-[-0.02em]">
          {t('session_end.analysing_title')}
        </DialogTitle>
        <p className="mt-1.5 text-sm text-muted-foreground">{t('session_end.analysing_subtitle')}</p>

        <ul className="mt-6 w-full space-y-2.5 text-left" aria-live="polite">
          {STEPS.map((name, index) => {
            const done = index < step;
            const active = index === step;
            return (
              <li
                key={name}
                data-state={done ? 'done' : active ? 'active' : 'waiting'}
                className="flex items-center gap-3 text-sm transition-opacity duration-300 data-[state=waiting]:opacity-40"
              >
                <span
                  aria-hidden
                  className={
                    done
                      ? 'flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground'
                      : 'h-5 w-5 rounded-full border-[1.5px] border-foreground/25'
                  }
                >
                  {done ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                </span>
                <span className={active ? 'font-medium' : undefined}>{t(`session_end.analysing_step_${name}`)}</span>
              </li>
            );
          })}
        </ul>

        <Button variant="ghost" className="mt-5" onClick={onDone}>
          {t('session_end.analysing_skip')}
        </Button>
      </div>
    </ExamDialog>
  );
}
